// =======================================================
// Memorias de Asignatura View (Subject Summaries & Reports)
// =======================================================

const MemoriasAsignaturaView = {
    selectedType: null, // first type with formations (Plan de Estudios → Tipos)
    selectedFormationId: null,
    reports: [],
    evaluations: [],

    init() {
        this.openSubjectId = null;
        this.selectedType = null;
        this.selectedFormationId = null;
        this.reports = [];
        this.evaluations = [];
        this.render();
    },

    async selectType(type) {
        this.selectedType = type;
        const formations = this.getFilteredFormations();
        const firstFormInType = formations.find(f => (f.type || f.category || 'OTRO') === type);
        if (firstFormInType) {
            await this.selectFormation(firstFormInType.id);
        } else {
            this.selectedFormationId = null;
            this.render();
        }
    },

    async selectFormation(formationId) {
        this.selectedFormationId = formationId;
        this.reports = [];
        this.evaluations = [];
        await Promise.all([
            this.loadReports(formationId),
            this.loadEvaluations(formationId)
        ]);
        this.render();
    },

    getFilteredFormations() {
        let formations = DataManager.getFormations() || [];
        const currentUser = AuthManager.getCurrentUser();
        const isProfessor = currentUser && (currentUser.role === 'professor' || AuthManager.isProfessor());
        const isStudent = currentUser && (currentUser.role === 'student' || AuthManager.isStudent());

        if (isProfessor && !AuthManager.hasModule('memorias-asignatura')) {
            const profId = currentUser.professorId || currentUser.id;
            if (profId) {
                const profSubjects = DataManager.getAll(DataManager.KEYS.SUBJECTS).filter(s => String(s.professorId) === String(profId));
                const activeFormationIds = [...new Set(profSubjects.map(s => s.formationId))];
                formations = formations.filter(f => activeFormationIds.includes(f.id));
            } else {
                formations = [];
            }
        }

        if (isStudent) {
            const studentId = currentUser.studentId || currentUser.id;
            if (studentId) {
                const student = DataManager.getById(DataManager.KEYS.STUDENTS, studentId);
                if (student && student.formations) {
                    formations = formations.filter(f => student.formations.map(String).includes(String(f.id)));
                } else {
                    formations = [];
                }
            } else {
                formations = [];
            }
        }

        return formations;
    },

    async loadReports(formationId) {
        try {
            const token = AuthManager.getCsrfToken();
            const res = await fetch(`/api/subject-reports?formationId=${formationId}`, {
                headers: { 'X-SICAF-CSRF': token }
            });
            const data = await res.json();
            if (data.success) {
                this.reports = data.reports;
            }
        } catch (e) {
            console.error('Error loading subject reports:', e);
        }
    },

    async loadEvaluations(formationId) {
        try {
            const token = AuthManager.getCsrfToken();
            const res = await fetch(`/api/student-subject-evaluations?formationId=${formationId}`, {
                headers: { 'X-SICAF-CSRF': token }
            });
            const data = await res.json();
            if (data.success) {
                this.evaluations = data.evaluations || [];
            }
        } catch (e) {
            console.error('Error loading student subject evaluations:', e);
        }
    },

    getBgColorForScore(score) {
        if (score === 'APTO') return '#dcfce7'; // green-100
        if (score === 'AVISO') return '#fef3c7'; // amber-100
        if (score === 'NO APTO') return '#fee2e2'; // red-100
        return '#f1f5f9'; // slate-100
    },

    getTextColorForScore(score) {
        if (score === 'APTO') return '#15803d'; // green-700
        if (score === 'AVISO') return '#d97706'; // amber-700
        if (score === 'NO APTO') return '#b91c1c'; // red-700
        return '#475569'; // slate-600
    },

    // Memorias, diseño A (2026-10-26): tipo y formación como botones, cuatro cifras y una tabla con
    // una fila por asignatura (estado de la memoria, resumen, barra de evaluaciones y acciones).
    // Todas cerradas al entrar; al pulsar una se ve su memoria entera y sus alumnos.
    // Estilos: assets/css/views/memorias-asignatura.css (.msa-*).
    toggleSubject(subjectId) {
        this.openSubjectId = this.openSubjectId === subjectId ? null : subjectId;
        this.render();
    },

    render() {
        const container = document.getElementById('content-area');
        if (!container) return;

        const allFormations = this.getFilteredFormations();
        const currentUser = AuthManager.getCurrentUser();
        const isStudent = currentUser && (currentUser.role === 'student' || AuthManager.isStudent());
        const esc = value => Utils.sanitizeHTML(String(value ?? ''));

        const types = [...new Set(allFormations.map(f => f.type || f.category || 'OTRO'))];
        if (types.length > 0 && (!this.selectedType || !types.includes(this.selectedType))) {
            // First type in the school's order (Plan de Estudios → Tipos).
            this.selectedType = FormationTypeService.getTypes().map(t => t.id).find(id => types.includes(id)) || types[0];
        }
        const visibleFormations = allFormations.filter(f => (f.type || f.category || 'OTRO') === this.selectedType);

        if ((!this.selectedFormationId || !visibleFormations.some(f => f.id === this.selectedFormationId)) && visibleFormations.length > 0) {
            this.selectedFormationId = visibleFormations[0].id;
            Promise.all([
                this.loadReports(this.selectedFormationId),
                this.loadEvaluations(this.selectedFormationId)
            ]).then(() => this.render());
            return;
        }

        const selectedFormation = allFormations.find(f => f.id === this.selectedFormationId);
        const subjects = selectedFormation ? DataManager.getSubjectsByFormation(this.selectedFormationId) : [];
        const students = (DataManager.getAll(DataManager.KEYS.STUDENTS) || [])
            .filter(s => s.formations && s.formations.map(String).includes(String(this.selectedFormationId)));
        const graded = this.evaluations.filter(e => e.grade && subjects.some(s => s.id === e.subjectId));
        const written = subjects.filter(s => this.reports.find(r => r.subjectId === s.id && r.content)).length;
        const warnings = graded.filter(e => e.grade === 'AVISO');
        const fails = graded.filter(e => e.grade === 'NO APTO');
        const possible = students.length * subjects.length;
        const failSubjects = [...new Set(fails.map(e => subjects.find(s => s.id === e.subjectId)?.name).filter(Boolean))];

        const chip = (on, label, handler, value) => `
            <button type="button" class="msa-chip ${on ? 'is-on' : ''}" aria-pressed="${on}" ${UIEvents.attr("click", handler, [value])}>${esc(label)}</button>`;
        const stat = (label, value, sub, tone = '', meter = null) => `
            <div class="msa-stat ${tone}">
                <span class="msa-stat-l">${label}</span>
                <span class="msa-stat-n">${value}</span>
                ${meter !== null ? `<span class="msa-meter" aria-hidden="true"><i style="width: ${meter}%"></i></span>` : ''}
                <span class="msa-stat-s" title="${esc(sub)}">${esc(sub)}</span>
            </div>`;

        const rows = subjects.map(subject => {
            const prof = DataManager.getProfessor(subject.professorId);
            const report = this.reports.find(r => r.subjectId === subject.id);
            const hasReport = !!(report && report.content);
            const isAssignedProf = currentUser && currentUser.professorId && String(currentUser.professorId) === String(subject.professorId);
            const canEdit = currentUser && (AuthManager.canManageModule('memorias-asignatura') || isAssignedProf);
            const open = this.openSubjectId === subject.id;
            const color = /^#[0-9a-f]{3,8}$/i.test(subject.color || '') ? subject.color : '#0d9488';
            const evals = this.evaluations.filter(e => e.subjectId === subject.id && e.grade);
            const count = grade => evals.filter(e => e.grade === grade).length;
            const total = Math.max(1, students.length);
            const myEval = isStudent ? this.evaluations.find(e => e.subjectId === subject.id) : null;
            const updated = hasReport && report.updatedAt ? new Date(report.updatedAt).toLocaleDateString('es-ES', { day: 'numeric', month: 'short' }) : '';
            const summary = hasReport ? String(report.content).replace(/\s+/g, ' ').trim() : '';
            return `
                <tr class="msa-row ${open ? 'is-open' : ''}" data-subject-id="${esc(subject.id)}">
                    <td>
                        <button type="button" class="msa-subject" aria-expanded="${open}" style="--msa-color: ${color}"
                            ${UIEvents.attr("click", function(event,captured){MemoriasAsignaturaView.toggleSubject(String(captured[0]));}, [subject.id])}>
                            <span class="msa-subject-name"><i class="msa-dot" aria-hidden="true"></i>${esc(subject.name)}<i class="fas fa-chevron-down msa-chevron" aria-hidden="true"></i></span>
                            <span class="msa-prof">${prof ? esc(`${prof.firstName} ${prof.lastName}`) : 'Sin asignar'}</span>
                        </button>
                    </td>
                    <td>
                        <span class="msa-status ${hasReport ? 'is-done' : ''}">${hasReport ? 'Redactada' : 'Pendiente'}</span>
                        ${updated ? `<span class="msa-date">${esc(updated)}</span>` : ''}
                    </td>
                    <td class="msa-summary">${hasReport ? `<span class="msa-summary-text">${esc(summary)}</span>` : '<span class="msa-muted">Sin redactar todavía.</span>'}</td>
                    <td>
                        ${isStudent ? (myEval && myEval.grade ? `<span class="msa-grade" data-grade="${esc(myEval.grade)}">${esc(myEval.grade)}</span>` : '<span class="msa-muted">Evaluación pendiente</span>') : `
                        <span class="msa-bar" aria-hidden="true">
                            <i class="is-apto" style="width: ${count('APTO') / total * 100}%"></i><i class="is-aviso" style="width: ${count('AVISO') / total * 100}%"></i><i class="is-no" style="width: ${count('NO APTO') / total * 100}%"></i>
                        </span>
                        <span class="msa-bar-text">${evals.length ? [[count('APTO'), 'apto', 'aptos'], [count('AVISO'), 'aviso', 'avisos'], [count('NO APTO'), 'no apto', 'no aptos']].map(([n, one, many]) => `${n} ${n === 1 ? one : many}`).join(' · ') : `Sin evaluar (${students.length} ${students.length === 1 ? 'alumno' : 'alumnos'})`}</span>`}
                        <button type="button" class="msa-toggle" aria-expanded="${open}" aria-label="${open ? 'Recoger' : (isStudent ? 'Ver la memoria de' : 'Ver los alumnos de')} ${esc(subject.name)}"
                            ${UIEvents.attr("click", function(event,captured){MemoriasAsignaturaView.toggleSubject(String(captured[0]));}, [subject.id])}>
                            ${open ? 'Recoger' : (isStudent ? 'Ver memoria' : 'Ver alumnos')} <i class="fas fa-chevron-down" aria-hidden="true"></i>
                        </button>
                    </td>
                    <td>
                        <span class="msa-actions">
                            ${canEdit ? `<button type="button" class="msa-btn" ${UIEvents.attr("click", function(event,captured){MemoriasAsignaturaView.showEditReportModal(String(captured[0]),String(captured[1]));}, [subject.id, subject.name])}>${hasReport ? 'Editar memoria' : 'Redactar'}</button>` : ''}
                            ${canEdit && !isStudent ? `<button type="button" class="msa-btn is-soft" ${UIEvents.attr("click", function(event,captured){MemoriasAsignaturaView.showEditEvaluationsModal(String(captured[0]),String(captured[1]));}, [subject.id, subject.name])}>Evaluar</button>` : ''}
                        </span>
                    </td>
                </tr>
                ${open ? `
                <tr class="msa-detail"><td colspan="5">
                    <div class="msa-detail-grid ${isStudent ? 'is-single' : ''}">
                        <div>
                            <span class="msa-label">Memoria completa</span>
                            ${hasReport ? `<p class="msa-full">${esc(report.content)}</p><span class="msa-date">Actualizada el ${esc(updated)} por ${esc(report.professorName)}</span>` : '<p class="msa-muted">Sin redactar todavía.</p>'}
                        </div>
                        ${isStudent ? (myEval && myEval.comment ? `<div><span class="msa-label">Comentario del docente</span><p class="msa-full">${esc(myEval.comment)}</p></div>` : '') : `
                        <div>
                            <span class="msa-label">Alumnos</span>
                            ${this.renderEvaluationsSummary(subject.id)}
                        </div>`}
                    </div>
                </td></tr>` : ''}`;
        }).join('');

        const html = `
            <div class="dnr-proposal msa-view">
                <header class="msa-head"><h1 class="msa-title">Memorias de asignatura</h1></header>
                ${types.length ? `
                <div class="msa-bar-filters">
                    <div class="msa-group" role="group" aria-label="Tipo de formación">
                        <span class="msa-bar-label">Tipo</span>
                        ${types.map(t => chip(this.selectedType === t, FormationTypeService.plural(t), function(event,captured){MemoriasAsignaturaView.selectType(String(captured[0]));}, t)).join('')}
                    </div>
                    <div class="msa-group" role="group" aria-label="Formación">
                        <span class="msa-bar-label">Formación</span>
                        ${visibleFormations.length ? visibleFormations.map(f => chip(String(f.id) === String(this.selectedFormationId), f.name, function(event,captured){MemoriasAsignaturaView.openSubjectId=null;MemoriasAsignaturaView.selectFormation(String(captured[0]));}, f.id)).join('') : '<span class="msa-muted">No hay formaciones disponibles</span>'}
                    </div>
                </div>` : ''}
                ${selectedFormation && !isStudent ? `
                <div class="msa-stats">
                    ${stat('Memorias redactadas', `${written} / ${subjects.length}`, subjects.length ? `${Math.round(written / subjects.length * 100)} % de las asignaturas` : 'sin asignaturas', 'is-teal', subjects.length ? Math.round(written / subjects.length * 100) : 0)}
                    ${stat('Alumnos evaluados', possible ? `${Math.round(graded.length / possible * 100)} %` : '—', possible ? `${graded.length} de ${possible} valoraciones` : 'sin alumnos matriculados')}
                    ${stat('Avisos', warnings.length, warnings.length ? `en ${new Set(warnings.map(e => e.studentId)).size} alumnos` : 'ninguno', warnings.length ? 'is-amber' : '')}
                    ${stat('No aptos', fails.length, failSubjects.length ? failSubjects.join(' · ') : 'ninguno', fails.length ? 'is-red' : '')}
                </div>` : ''}
                <section class="msa-card">
                    ${selectedFormation ? `
                    <div class="msa-card-head">
                        <h2 class="msa-h2">${esc(selectedFormation.name)} · ${subjects.length} ${subjects.length === 1 ? 'asignatura' : 'asignaturas'}</h2>
                        ${subjects.length ? '<span class="msa-hint">Pulsa una asignatura para leer su memoria entera' + (isStudent ? '' : ' y ver a sus alumnos') + '</span>' : ''}
                    </div>
                    ${subjects.length ? `
                    <div class="msa-table-wrap">
                        <table class="msa-table">
                            <thead><tr><th scope="col">Asignatura y docente</th><th scope="col">Memoria</th><th scope="col">Resumen</th><th scope="col">${isStudent ? 'Mi evaluación' : 'Alumnos'}</th><th scope="col"><span class="msa-sr">Acciones</span></th></tr></thead>
                            <tbody>${rows}</tbody>
                        </table>
                    </div>` : '<p class="msa-empty">No hay asignaturas registradas en esta formación.</p>'}` : '<p class="msa-empty">Selecciona una formación para ver sus memorias.</p>'}
                </section>
            </div>`;

        SafeHTML.set(container, html);
    },

    renderEvaluationsSummary(subjectId) {
        const allStudents = typeof DataManager !== 'undefined' ? DataManager.getAll(DataManager.KEYS.STUDENTS) || [] : [];
        const students = allStudents.filter(s => s.formations && s.formations.map(String).includes(String(this.selectedFormationId)));
        if (students.length === 0) return '<p class="msa-muted">No hay alumnos matriculados en esta formación.</p>';
        const subjectEvals = this.evaluations.filter(e => e.subjectId === subjectId);
        return `
            <div class="msa-students">
                ${students.map(student => {
                    const sEval = subjectEvals.find(e => e.studentId === student.id);
                    const grade = sEval ? sEval.grade : '';
                    const comment = sEval ? sEval.comment : '';
                    return `
                        <div class="msa-student">
                            <b>${Utils.sanitizeHTML(student.firstName)} ${Utils.sanitizeHTML(student.lastName)}</b>
                            ${grade ? `<span class="msa-grade" data-grade="${Utils.sanitizeHTML(grade)}">${Utils.sanitizeHTML(grade)}</span>` : '<span class="msa-grade">Sin evaluar</span>'}
                            <span class="msa-comment" title="${Utils.sanitizeHTML(comment || '')}">${comment ? Utils.sanitizeHTML(comment) : 'Sin comentarios'}</span>
                        </div>`;
                }).join('')}
            </div>`;
    },

    showEditEvaluationsModal(subjectId, subjectName) {
        const allStudents = typeof DataManager !== 'undefined' ? DataManager.getAll(DataManager.KEYS.STUDENTS) || [] : [];
        const students = allStudents.filter(s => s.formations && s.formations.map(String).includes(String(this.selectedFormationId)));
        const subjectEvals = this.evaluations.filter(e => e.subjectId === subjectId);

        const modalHtml = `
            <form id="edit-evaluations-form" ${UIEvents.attr("submit", function(event,captured){MemoriasAsignaturaView.handleSaveEvaluations(event,String(captured[0]));}, [subjectId])} style="font-family:system-ui; text-align:left;">
                <div style="max-height:400px; overflow-y:auto; border:1px solid #e2e8f0; border-radius:8px; margin-bottom:1.5rem; background:#f8fafc;">
                    <table style="width:100%; border-collapse:collapse; font-size:0.82rem;">
                        <thead>
                            <tr style="background:#e2e8f0; border-bottom:1px solid #cbd5e1; color:#475569; text-align:left; position:sticky; top:0;">
                                <th style="padding:8px 12px;">Alumno</th>
                                <th style="padding:8px 12px; width:130px; text-align:center;">Resultado</th>
                                <th style="padding:8px 12px;">Observaciones</th>
                            </tr>
                        </thead>
                        <tbody>
                            ${students.map(student => {
                                const sEval = subjectEvals.find(e => e.studentId === student.id);
                                const currentGrade = sEval ? sEval.grade : '';
                                const currentComment = sEval ? sEval.comment : '';
                                
                                return `
                                <tr style="border-bottom:1px solid #e2e8f0; background:white;">
                                    <td style="padding:10px 12px; font-weight:600; color:#1e293b;">
                                        ${Utils.sanitizeHTML(student.firstName)} ${Utils.sanitizeHTML(student.lastName)}
                                        <input type="hidden" name="studentId" value="${Utils.sanitizeHTML(student.id)}">
                                        <input type="hidden" name="studentName" value="${Utils.sanitizeHTML(student.firstName)} ${Utils.sanitizeHTML(student.lastName)}">
                                    </td>
                                    <td style="padding:10px 12px; text-align:center;">
                                        <select name="grade" ${UIEvents.attr("change", function(event,captured){CalificacionesAdminView.updateCellColor(this);}, [])}
                                                style="padding:4px 6px; border-radius:6px; border:1.5px solid #cbd5e1; font-weight:700; width:100%; cursor:pointer; background:${CalificacionesAdminView.getBgColorForScore(currentGrade)}; color:${CalificacionesAdminView.getTextColorForScore(currentGrade)};">
                                            <option value="" ${!currentGrade ? 'selected' : ''}>-</option>
                                            <option value="APTO" ${currentGrade === 'APTO' ? 'selected' : ''} style="color:#166534; font-weight:700;">APTO</option>
                                            <option value="AVISO" ${currentGrade === 'AVISO' ? 'selected' : ''} style="color:#92400e; font-weight:700;">AVISO</option>
                                            <option value="NO APTO" ${currentGrade === 'NO APTO' ? 'selected' : ''} style="color:#991b1b; font-weight:700;">NO APTO</option>
                                        </select>
                                    </td>
                                    <td style="padding:10px 12px;">
                                        <input type="text" name="comment" value="${Utils.sanitizeHTML(currentComment)}" placeholder="Comentario sobre el alumno..." 
                                               style="width:100%; padding:5px 8px; border-radius:6px; border:1px solid #cbd5e1; font-size:0.8rem;">
                                    </td>
                                </tr>`;
                            }).join('')}
                        </tbody>
                    </table>
                </div>

                <div style="display:flex; gap:0.5rem; justify-content:flex-end;">
                    <button type="button" class="btn btn-secondary" ${UIEvents.attr("click", function(event,captured){Utils.closeModal();}, [])}>Cancelar</button>
                    <button type="submit" class="btn btn-primary"><i class="fas fa-save"></i> Guardar Evaluaciones</button>
                </div>
            </form>
        `;

        Utils.openModal(`Evaluación Alumnos: ${subjectName}`, modalHtml);
    },

    async handleSaveEvaluations(event, subjectId) {
        event.preventDefault();
        const form = event.target;
        
        // Parse bulk inputs
        const rows = form.querySelectorAll('tbody tr');
        const evaluations = [];
        
        rows.forEach(row => {
            const studentId = row.querySelector('input[name="studentId"]').value;
            const studentName = row.querySelector('input[name="studentName"]').value;
            const grade = row.querySelector('select[name="grade"]').value;
            const comment = row.querySelector('input[name="comment"]').value;
            
            evaluations.push({ studentId, studentName, grade, comment });
        });

        try {
            const token = AuthManager.getCsrfToken();
            const res = await fetch('/api/student-subject-evaluations', {
                method: 'POST',
                headers: {
                    'X-SICAF-CSRF': token,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    formationId: this.selectedFormationId,
                    subjectId,
                    evaluations
                })
            });

            const data = await res.json();
            if (data.success) {
                Utils.showToast('Evaluaciones de alumnos guardadas con éxito', 'success');
                Utils.closeModal();
                await this.loadEvaluations(this.selectedFormationId);
                this.render();
            } else {
                SafeHTML.fire('Error', data.error || 'No se pudieron guardar las evaluaciones', 'error');
            }
        } catch(e) {
            console.error(e);
            SafeHTML.fire('Error', 'No se pudieron guardar las evaluaciones', 'error');
        }
    },

    showEditReportModal(subjectId, subjectName) {
        const report = this.reports.find(r => r.subjectId === subjectId);
        const currentContent = report ? report.content : '';

        const modalHtml = `
            <form id="edit-report-form" ${UIEvents.attr("submit", function(event,captured){MemoriasAsignaturaView.handleSaveReport(event,String(captured[0]));}, [subjectId])} style="font-family:system-ui; text-align:left;">
                <p style="color:#64748b; font-size:0.88rem; margin-bottom:1rem;">
                    Escribe un resumen explicativo detallando el temario impartido, proyectos realizados y valoración final de la asignatura.
                </p>
                <div class="form-group" style="margin-bottom:1.5rem;">
                    <label class="form-label" style="font-weight:700; font-size:0.8rem; color:#475569; display:block; margin-bottom:6px;">Explicación / Memoria de Asignatura *</label>
                    <textarea name="content" class="form-input" rows="8" placeholder="Escribe aquí..." style="width:100%; padding:10px; font-family:inherit; resize:none; border:1px solid #cbd5e1; border-radius:8px;" required>${Utils.sanitizeHTML(currentContent)}</textarea>
                </div>
                <div style="display:flex; gap:0.5rem; justify-content:flex-end;">
                    <button type="button" class="btn btn-secondary" ${UIEvents.attr("click", function(event,captured){Utils.closeModal();}, [])}>Cancelar</button>
                    <button type="submit" class="btn btn-primary"><i class="fas fa-save"></i> Guardar Memoria</button>
                </div>
            </form>
        `;

        Utils.openModal(`Memoria: ${subjectName}`, modalHtml);
    },

    async handleSaveReport(event, subjectId) {
        event.preventDefault();
        const formData = new FormData(event.target);
        const content = formData.get('content');

        const currentUser = AuthManager.getCurrentUser();
        const professorName = currentUser ? currentUser.name : 'Profesor';

        try {
            const token = AuthManager.getCsrfToken();
            const res = await fetch('/api/subject-reports', {
                method: 'POST',
                headers: {
                    'X-SICAF-CSRF': token,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    formationId: this.selectedFormationId,
                    subjectId,
                    content,
                    professorName
                })
            });

            const data = await res.json();
            if (data.success) {
                Utils.showToast('Memoria de asignatura guardada correctamente', 'success');
                Utils.closeModal();
                await this.loadReports(this.selectedFormationId);
                this.render();
            } else {
                SafeHTML.fire('Error', data.error || 'No se pudo guardar la memoria', 'error');
            }
        } catch (e) {
            console.error(e);
            SafeHTML.fire('Error', 'No se pudo guardar la memoria', 'error');
        }
    }
};

window.MemoriasAsignaturaView = MemoriasAsignaturaView;
